// Raw HTTP client for the PRR-48 Person/Worker API. Mirrors organisationHttpClient:
// every call goes through `authFetch` (Bearer token + 401 refresh) and returns the parsed
// `ApiResponse<T>` envelope.
//
// Unlike a throw-on-error client, this parser returns the envelope for ALL status codes so
// callers can surface backend business-rule messages (e.g. BR-CT-001 last-email 409,
// BR-AD-001 residential-retention 409, terminated-person 409). It only synthesises a failure
// envelope when the body is missing or not JSON (network/5xx without a body).

import { authFetch } from '../../auth/authFetch';
import { getApiBaseUrl } from '../../auth/api';
import type {
  ApiResponse,
  CreatePersonAddressRequest,
  CreatePersonContactRequest,
  CreatePersonRequest,
  PersonAddressResponse,
  PersonContactResponse,
  PersonDetailResponse,
  PersonEmergencyContactResponse,
  PersonListParams,
  UpdatePersonAddressRequest,
  UpdatePersonContactRequest,
  UpdatePersonDetailRequest,
} from './workersApiTypes';

const base = () => `${getApiBaseUrl()}/api/person`;

const jsonInit = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

const parse = async <T>(response: Response): Promise<ApiResponse<T>> => {
  // Read as text first so an empty body (e.g. a REST-conventional 204 No Content on DELETE) does
  // not throw in response.json() and get mis-reported as a failure.
  const text = await response.text();
  const failure: ApiResponse<T> = { success: false, statusCode: response.status, message: `Request failed: ${response.status}` };
  if (!text) return response.ok ? { success: true, statusCode: response.status } : failure;
  try {
    const body = JSON.parse(text) as ApiResponse<T>;
    if (body && typeof body.success === 'boolean') return body;
    return response.ok ? { success: true, statusCode: response.status, result: body as unknown as T } : failure;
  } catch {
    return response.ok ? { success: true, statusCode: response.status } : failure;
  }
};

const buildListQuery = (params: PersonListParams): string => {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.globalEmployeeId) search.set('globalEmployeeId', params.globalEmployeeId);
  if (params.effectiveDate) search.set('effectiveDate', params.effectiveDate);
  if (params.take !== undefined) search.set('take', String(params.take));
  if (params.skip !== undefined) search.set('skip', String(params.skip));
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

// --- Person ---

export const fetchPersons = async (params: PersonListParams = {}): Promise<ApiResponse<PersonDetailResponse[]>> =>
  parse<PersonDetailResponse[]>(await authFetch(`${base()}${buildListQuery(params)}`));

export const fetchPersonById = async (personId: string, effectiveDate?: string): Promise<ApiResponse<PersonDetailResponse>> => {
  const qs = effectiveDate ? `?effectiveDate=${encodeURIComponent(effectiveDate)}` : '';
  return parse<PersonDetailResponse>(await authFetch(`${base()}/${personId}${qs}`));
};

export const createPerson = async (request: CreatePersonRequest): Promise<ApiResponse<PersonDetailResponse>> =>
  parse<PersonDetailResponse>(await authFetch(base(), jsonInit('POST', request)));

export const updatePersonDetail = async (personId: string, request: UpdatePersonDetailRequest): Promise<ApiResponse<PersonDetailResponse>> =>
  parse<PersonDetailResponse>(await authFetch(`${base()}/${personId}/detail`, jsonInit('PUT', request)));

// --- Contacts ---

export const fetchContacts = async (personId: string): Promise<ApiResponse<PersonContactResponse[]>> =>
  parse<PersonContactResponse[]>(await authFetch(`${base()}/${personId}/contact`));

export const createContact = async (personId: string, request: CreatePersonContactRequest): Promise<ApiResponse<PersonContactResponse>> =>
  parse<PersonContactResponse>(await authFetch(`${base()}/${personId}/contact`, jsonInit('POST', request)));

export const updateContact = async (personId: string, contactId: string, request: UpdatePersonContactRequest): Promise<ApiResponse<PersonContactResponse>> =>
  parse<PersonContactResponse>(await authFetch(`${base()}/${personId}/contact/${contactId}`, jsonInit('PUT', request)));

export const deleteContact = async (personId: string, contactId: string): Promise<ApiResponse<PersonContactResponse>> =>
  parse<PersonContactResponse>(await authFetch(`${base()}/${personId}/contact/${contactId}`, { method: 'DELETE' }));

// --- Addresses ---

export const fetchAddresses = async (personId: string): Promise<ApiResponse<PersonAddressResponse[]>> =>
  parse<PersonAddressResponse[]>(await authFetch(`${base()}/${personId}/address`));

export const createAddress = async (personId: string, request: CreatePersonAddressRequest): Promise<ApiResponse<PersonAddressResponse>> =>
  parse<PersonAddressResponse>(await authFetch(`${base()}/${personId}/address`, jsonInit('POST', request)));

export const updateAddress = async (personId: string, addressId: string, request: UpdatePersonAddressRequest): Promise<ApiResponse<PersonAddressResponse>> =>
  parse<PersonAddressResponse>(await authFetch(`${base()}/${personId}/address/${addressId}`, jsonInit('PUT', request)));

export const deleteAddress = async (personId: string, addressId: string): Promise<ApiResponse<PersonAddressResponse>> =>
  parse<PersonAddressResponse>(await authFetch(`${base()}/${personId}/address/${addressId}`, { method: 'DELETE' }));

// --- Emergency contacts (read-only) ---

export const fetchEmergencyContacts = async (personId: string): Promise<ApiResponse<PersonEmergencyContactResponse[]>> =>
  parse<PersonEmergencyContactResponse[]>(await authFetch(`${base()}/${personId}/emergency-contact`));

// --- Reference data ---

export const fetchTitles = async (): Promise<ApiResponse<string[]>> => parse<string[]>(await authFetch(`${base()}/titles`));
export const fetchGenders = async (): Promise<ApiResponse<string[]>> => parse<string[]>(await authFetch(`${base()}/genders`));
export const fetchContactTypes = async (): Promise<ApiResponse<string[]>> => parse<string[]>(await authFetch(`${base()}/contact-types`));
export const fetchAddressTypes = async (): Promise<ApiResponse<string[]>> => parse<string[]>(await authFetch(`${base()}/address-types`));
